import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getCheckout, getPaymentStatus } from '../utils/api';

/* ── constants ── */
const CRYPTO_NAMES = { BTC:'Bitcoin', ETH:'Ethereum', LTC:'Litecoin', USDT_ERC20:'USDT (ERC20)', USDC_ERC20:'USDC (ERC20)', USDT_BEP20:'USDT (BEP20)', PENDING:'Crypto' };
const CRYPTO_SYMBOLS = { btc:'BTC', eth:'ETH', ltc:'LTC', usdterc20:'USDT', usdcerc20:'USDC', usdtbep20:'USDT' };
const CRYPTO_COLORS = { BTC:'#f7931a', ETH:'#627eea', LTC:'#bfbbbb', USDT_ERC20:'#26a17b', USDC_ERC20:'#2775ca', USDT_BEP20:'#26a17b', PENDING:'#8b5cf6' };
const STATUS_LABELS = { waiting:'Awaiting Payment', confirming:'Confirming…', confirmed:'Confirmed', sending:'Processing' };
const STEP_LABELS = ['Payment','Confirming','Processing','Complete'];

/* ── inject styles once ── */
const injectOnce = (() => {
  let done = false;
  return () => {
    if (done) return; done = true;
    const s = document.createElement('style');
    s.textContent = `
      @keyframes ck-enter{0%{opacity:0;transform:translateY(20px)}100%{opacity:1;transform:translateY(0)}}
      @keyframes ck-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
      @keyframes ck-glow{0%,100%{opacity:.45}50%{opacity:.85}}
      @keyframes ck-dot{0%,100%{opacity:1}50%{opacity:.25}}
      @keyframes ck-shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(200%)}}
      @keyframes ck-ring{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
      @keyframes ck-breathe{0%,100%{box-shadow:0 0 20px rgba(46,232,197,.15)}50%{box-shadow:0 0 40px rgba(46,232,197,.3)}}
      @keyframes ck-confetti-1{0%{transform:translate(0,0) rotate(0) scale(1);opacity:1}100%{transform:translate(-60px,-120px) rotate(200deg) scale(0);opacity:0}}
      @keyframes ck-confetti-2{0%{transform:translate(0,0) rotate(0) scale(1);opacity:1}100%{transform:translate(50px,-100px) rotate(-180deg) scale(0);opacity:0}}
      @keyframes ck-confetti-3{0%{transform:translate(0,0) rotate(0) scale(1);opacity:1}100%{transform:translate(70px,-80px) rotate(160deg) scale(0);opacity:0}}
      @keyframes ck-confetti-4{0%{transform:translate(0,0) rotate(0) scale(1);opacity:1}100%{transform:translate(-40px,-130px) rotate(-220deg) scale(0);opacity:0}}
      .ck-enter{animation:ck-enter .55s cubic-bezier(.22,1,.36,1) both}
      .ck-d1{animation-delay:.06s}.ck-d2{animation-delay:.12s}.ck-d3{animation-delay:.18s}.ck-d4{animation-delay:.24s}.ck-d5{animation-delay:.30s}
      .ck-page{min-height:100vh;display:flex;flex-direction:column;align-items:center;padding:1.5rem 1rem 4rem;position:relative;overflow-x:hidden}
      .ck-page::before{content:'';position:fixed;top:-20%;left:-15%;width:55vw;height:55vw;max-width:650px;max-height:650px;border-radius:50%;background:radial-gradient(circle,rgba(46,232,197,.1) 0%,transparent 65%);filter:blur(80px);pointer-events:none}
      .ck-page::after{content:'';position:fixed;bottom:-15%;right:-12%;width:50vw;height:50vw;max-width:600px;max-height:600px;border-radius:50%;background:radial-gradient(circle,rgba(139,92,246,.08) 0%,transparent 65%);filter:blur(80px);pointer-events:none}
      .ck-brand{display:inline-flex;align-items:center;gap:6px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.07);border-radius:999px;padding:5px 14px 5px 8px;font-size:.72rem;color:var(--text-secondary);margin-bottom:1.5rem;text-decoration:none;backdrop-filter:blur(12px);transition:all .25s}
      .ck-brand:hover{border-color:rgba(255,255,255,.12);color:var(--text-primary)}
      .ck-brand img{width:20px;height:20px;border-radius:5px;object-fit:contain}
      .ck-title{font-size:clamp(1.4rem,3.5vw,1.8rem);font-weight:800;letter-spacing:-.03em;text-align:center;margin-bottom:4px}
      .ck-sub{font-size:.85rem;color:var(--text-secondary);text-align:center;margin-bottom:2rem}
      .ck-glass{background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.07);border-radius:20px;padding:1.75rem;backdrop-filter:blur(20px);position:relative;overflow:hidden}
      .ck-glass-glow{position:absolute;top:-1px;left:15%;right:15%;height:2px;border-radius:1px;opacity:.5;animation:ck-glow 3s ease-in-out infinite}
      .ck-grid{display:grid;grid-template-columns:1.05fr .95fr;gap:1.25rem;max-width:880px;width:100%;position:relative;z-index:1}
      @media(max-width:760px){.ck-grid{grid-template-columns:1fr}}
      .ck-row{display:flex;justify-content:space-between;align-items:center;padding:.7rem 0;border-bottom:1px solid rgba(255,255,255,.04)}
      .ck-row:last-child{border-bottom:none}
      .ck-row-label{font-size:.78rem;color:var(--text-secondary)}
      .ck-row-val{font-size:.82rem;font-weight:600;text-align:right}
      .ck-copy{display:inline-flex;align-items:center;gap:5px;padding:5px 12px;border-radius:8px;font-size:.68rem;font-weight:600;cursor:pointer;transition:all .2s;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.05);color:var(--text-secondary)}
      .ck-copy:hover{background:rgba(255,255,255,.08);color:var(--text-primary)}
      .ck-copy.copied{background:rgba(46,232,197,.12);border-color:rgba(46,232,197,.25);color:var(--accent)}
      .ck-pill{display:inline-flex;align-items:center;gap:5px;padding:4px 12px;border-radius:999px;font-size:.72rem;font-weight:700}
      .ck-center-state{max-width:520px;text-align:center;position:relative;z-index:1}
      .ck-icon-ring{width:88px;height:88px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;margin-bottom:1.5rem;position:relative}
      .ck-btn{display:inline-flex;align-items:center;gap:8px;padding:.75rem 1.75rem;border-radius:12px;font-size:.85rem;font-weight:700;cursor:pointer;transition:all .25s;text-decoration:none;border:none}
      .ck-btn-primary{background:var(--accent);color:#0a0a0f;box-shadow:0 4px 20px rgba(46,232,197,.25)}
      .ck-btn-primary:hover{transform:translateY(-1px);box-shadow:0 6px 28px rgba(46,232,197,.35)}
      .ck-btn-ghost{background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);color:var(--text-secondary)}
      .ck-btn-ghost:hover{border-color:rgba(255,255,255,.18);color:var(--text-primary)}
      .ck-confetti{position:absolute;width:8px;height:8px;border-radius:2px;pointer-events:none}
    `;
    document.head.appendChild(s);
  };
})();

/* ── static components (outside render) ── */
const Brand = () => <Link to="/" className="ck-brand"><img src="/logo.png" alt="" /> Outsourced</Link>;

const OrderTag = ({ orderId }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 10, marginBottom: '1.5rem' }}>
    <span style={{ fontSize: '.62rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>ORDER</span>
    <code style={{ fontSize: '.7rem', color: 'var(--text-primary)', fontFamily: 'monospace' }}>{orderId?.slice(0, 8)}…</code>
  </div>
);

const StepperView = ({ step }) => (
  <div className="ck-enter ck-d1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2.25rem', gap: 0, flexWrap: 'wrap', zIndex: 1 }}>
    {STEP_LABELS.map((label, i) => {
      const n = i + 1, active = step >= n, done = step > n;
      return (
        <React.Fragment key={label}>
          {i > 0 && <div style={{ width: 44, height: 2, background: step > i ? 'var(--accent)' : 'rgba(255,255,255,.08)', transition: 'background .5s ease', flexShrink: 0 }} />}
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '.75rem', fontWeight: 700,
              border: `2px solid ${done ? 'var(--accent)' : active ? 'var(--accent)' : 'rgba(255,255,255,.08)'}`,
              background: done ? 'var(--accent)' : active ? 'rgba(46,232,197,.08)' : 'transparent',
              color: done ? '#0a0a0f' : active ? 'var(--accent)' : 'var(--text-muted)',
              transition: 'all .45s cubic-bezier(.22,1,.36,1)',
              boxShadow: done ? '0 0 16px rgba(46,232,197,.35)' : 'none',
            }}>
              {done ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg> : n}
            </div>
            <span style={{ position: 'absolute', bottom: -20, fontSize: '.6rem', fontWeight: 500, whiteSpace: 'nowrap', color: active ? 'var(--text-secondary)' : 'var(--text-muted)', transition: 'color .3s' }}>{label}</span>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

const CONFETTI_COLS = ['#2ee8c5', '#8b5cf6', '#f5a623', '#e84393', '#627eea'];
const CONFETTI_DATA = Array.from({ length: 12 }, (_, i) => ({ col: CONFETTI_COLS[i % CONFETTI_COLS.length], dur: 0.6 + (i * 0.037), anim: (i % 4) + 1, delay: i * 0.04, rot: i * 30 }));
const Confetti = () => (
  <div style={{ position: 'absolute', top: '50%', left: '50%', pointerEvents: 'none' }}>
    {CONFETTI_DATA.map((c, i) => (
      <div key={i} className="ck-confetti" style={{ background: c.col, animation: `ck-confetti-${c.anim} ${c.dur}s cubic-bezier(.22,1,.36,1) ${c.delay}s both`, transform: `rotate(${c.rot}deg)` }} />
    ))}
  </div>
);

const STATUS_COLORS = { waiting: { bg: 'rgba(245,158,11,.1)', c: '#f59e0b', b: 'rgba(245,158,11,.2)' }, confirming: { bg: 'rgba(46,232,197,.1)', c: '#3b82f6', b: 'rgba(46,232,197,.2)' }, confirmed: { bg: 'rgba(46,232,197,.1)', c: '#2ee8c5', b: 'rgba(46,232,197,.2)' }, sending: { bg: 'rgba(139,92,246,.1)', c: '#8b5cf6', b: 'rgba(139,92,246,.2)' } };

/* ── component ── */
const Checkout = () => {
  injectOnce();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [copied, setCopied] = useState({});
  const [paymentStatus, setPaymentStatus] = useState('waiting');
  const paymentStatusRef = useRef('waiting');
  const [boostStatus, setBoostStatus] = useState('pending');
  const isToken = checkout?.productId?.startsWith('token_');
  const isTool = checkout?.productId?.startsWith('tool_');
  const [orderTokens, setOrderTokens] = useState([]);
  const [orderLicenseKeys, setOrderLicenseKeys] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [stripeConfirming, setStripeConfirming] = useState(false);
  const paymentSuccess = searchParams.get('payment') === 'success';
  const paypalCapture = searchParams.get('paypal') === 'capture';

  // Review state
  const [canReview, setCanReview] = useState(false);
  const [reviewDiscount, setReviewDiscount] = useState(5);
  const [reviewCoupon, setReviewCoupon] = useState(null);
  const [reviewForm, setReviewForm] = useState({ stars: 5, text: '' });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('token'));

  /* ── data logic ── */
  const addHistoryEvent = useCallback((ev, det) => {
    setHistory(p => p.some(h => h.event === ev) ? p : [...p, { event: ev, details: det, time: new Date().toLocaleTimeString() }]);
  }, []);

  const fetchTokens = useCallback(() => {
    fetch(`/api/order/${orderId}/tokens`).then(r => r.json()).then(d => { if (d.tokens?.length) setOrderTokens(d.tokens); }).catch(() => {});
    fetch(`/api/order/${orderId}/license-keys`).then(r => r.json()).then(d => { if (d.keys?.length) setOrderLicenseKeys(d.keys); }).catch(() => {});
  }, [orderId]);

  const fetchCheckout = useCallback(async () => {
    try {
      const r = await getCheckout(orderId);
      if (r.expired) { setCheckout({ expired: true, ...r }); addHistoryEvent('Order Expired', 'Payment window has closed'); }
      else if (r.completed) { 
        setCheckout({ completed: true, ...r }); setBoostStatus(r.boostStatus || 'completed'); 
        addHistoryEvent('Order Completed', r.productId?.startsWith('token_') ? 'Tokens have been delivered' : r.productId?.startsWith('tool_') ? 'License key has been generated' : 'Boosts have been delivered');
        if (r.productId?.startsWith('token_') || r.productId?.startsWith('tool_')) fetchTokens();
      }
      else { setCheckout(r); setTimeRemaining(r.timeRemaining); const st = r.status || 'waiting'; setPaymentStatus(st); paymentStatusRef.current = st; setBoostStatus(r.boostStatus || 'pending'); if (r.paymentAddress) addHistoryEvent('Address Generated', r.paymentAddress); }
    } catch (e) { setError(e.message || 'Failed to load checkout'); }
    finally { setLoading(false); }
  }, [orderId, addHistoryEvent, fetchTokens]);

  const pollStatus = useCallback(async () => {
    try {
      const d = await getPaymentStatus(orderId);
      if (d.paymentStatus === 'confirming' && paymentStatusRef.current !== 'confirming') addHistoryEvent('Payment Detected', 'Transaction found on blockchain');
      if (d.status === 'confirmed' || d.boostStatus === 'completed') {
        setPaymentStatus('confirmed'); paymentStatusRef.current = 'confirmed'; setBoostStatus(d.boostStatus || 'completed');
        addHistoryEvent('Payment Confirmed', 'Transaction confirmed');
        if (d.boostStatus === 'completed') { 
          setCheckout(p => ({ ...p, completed: true, boostStatus: 'completed' })); 
          addHistoryEvent('Delivered', 'Order fulfilled');
          fetchTokens();
        }
      } else if (d.paymentStatus) { setPaymentStatus(d.paymentStatus); paymentStatusRef.current = d.paymentStatus; if (d.boostStatus) setBoostStatus(d.boostStatus); }
    } catch {}
  }, [orderId, addHistoryEvent, fetchTokens]);

  useEffect(() => { fetchCheckout(); if (window.trackFunnel) window.trackFunnel('checkout_start', { orderId }); }, [fetchCheckout]);

  useEffect(() => {
    if (paymentSuccess && checkout?.paymentMethod === 'stripe') {
      setStripeConfirming(true); addHistoryEvent('Payment Received', 'Stripe payment completed');
      const iv = setInterval(async () => {
        try {
          const r = await getCheckout(orderId);
          if (r.completed || r.boostStatus === 'completed' || r.boostStatus === 'failed') { setStripeConfirming(false); setCheckout(r); setBoostStatus(r.boostStatus || 'completed'); setPaymentStatus('confirmed'); clearInterval(iv); }
          else if (r.status === 'confirmed') { setPaymentStatus('confirmed'); setBoostStatus(r.boostStatus || 'processing'); }
        } catch {}
      }, 2000);
      return () => clearInterval(iv);
    }
    if (paypalCapture && checkout?.paypalOrderId) {
      setStripeConfirming(true);
      fetch('/api/capture-paypal-order', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ orderId, paypalOrderId: checkout.paypalOrderId }) })
        .then(r => r.json()).then(d => { if (d.success) { addHistoryEvent('Payment Captured', 'PayPal confirmed'); fetchCheckout(); } setStripeConfirming(false); })
        .catch(() => setStripeConfirming(false));
    }
  }, [paymentSuccess, paypalCapture, checkout?.paymentMethod, checkout?.paypalOrderId, orderId, addHistoryEvent, fetchCheckout]);

  useEffect(() => { if (checkout && !checkout.expired && !checkout.completed) { const iv = setInterval(pollStatus, 5000); return () => clearInterval(iv); } }, [!!checkout, checkout?.expired, checkout?.completed, pollStatus]);
  useEffect(() => {
    if (timeRemaining <= 0 || checkout?.completed) return;
    const iv = setInterval(() => setTimeRemaining(p => { if (p <= 1000) { clearInterval(iv); setCheckout(c => ({ ...c, expired: true })); return 0; } return p - 1000; }), 1000);
    return () => clearInterval(iv);
  }, [timeRemaining, checkout?.completed]);

  useEffect(() => {
    const tk = localStorage.getItem('token'); setIsLoggedIn(!!tk);
    if (boostStatus === 'completed' && orderId && tk)
      fetch(`/api/user/can-review/${orderId}`, { headers: { Authorization: `Bearer ${tk}` } })
        .then(r => r.json()).then(d => { if (d.canReview) { setCanReview(true); setReviewDiscount(d.discount || 5); } }).catch(() => {});
    if (boostStatus === 'completed' && orderId && isToken) fetchTokens();
  }, [boostStatus, orderId, isToken, fetchTokens]);

  const submitReview = async () => {
    const tk = localStorage.getItem('token'); if (!tk) return;
    setReviewLoading(true); setReviewError(null);
    try {
      const res = await fetch('/api/user/submit-review', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` }, body: JSON.stringify({ ...reviewForm, orderId }) });
      const d = await res.json(); if (!res.ok) throw new Error(d.error);
      setReviewSubmitted(true); setCanReview(false); if (d.coupon) setReviewCoupon(d.coupon);
    } catch (e) { setReviewError(e.message); } finally { setReviewLoading(false); }
  };

  /* ── helpers ── */
  const fmt = ms => { const s = Math.floor(ms / 1000); return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`; };
  const copy = async (txt, k) => { try { await navigator.clipboard.writeText(txt); setCopied(p => ({ ...p, [k]: true })); setTimeout(() => setCopied(p => ({ ...p, [k]: false })), 2200); } catch {} };
  const step = useMemo(() => { if (boostStatus === 'completed') return 4; if (paymentStatus === 'confirmed' || boostStatus === 'processing') return 3; if (paymentStatus === 'confirming') return 2; return 1; }, [paymentStatus, boostStatus]);
  const timerPct = useMemo(() => { const total = checkout?.timeoutMs || 1800000; return Math.max(0, Math.min(1, timeRemaining / total)); }, [timeRemaining, checkout?.timeoutMs]);
  const isUrgent = timeRemaining > 0 && timeRemaining < 120000; // <2min

  /* ═══════════════════════════════════════
     RENDER HELPERS (inline JSX, not components)
     ═══════════════════════════════════════ */

  const timerRingJsx = (() => {
    const r = 54, c = 2 * Math.PI * r;
    return (
      <div style={{ position: 'relative', width: 130, height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
        <svg width="130" height="130" viewBox="0 0 130 130" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="65" cy="65" r={r} fill="none" stroke="rgba(255,255,255,.04)" strokeWidth="5" />
          <circle cx="65" cy="65" r={r} fill="none" stroke={isUrgent ? '#f0485f' : 'var(--accent)'} strokeWidth="5" strokeDasharray={c} strokeDashoffset={c * (1 - timerPct)} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s linear, stroke .3s' }} />
        </svg>
        <div style={{ position: 'absolute', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: isUrgent ? '#f0485f' : 'var(--text-primary)', letterSpacing: '-.02em' }}>{fmt(timeRemaining)}</div>
          <div style={{ fontSize: '.6rem', fontWeight: 500, color: isUrgent ? 'rgba(240,72,95,.7)' : 'var(--text-muted)', letterSpacing: '.04em', textTransform: 'uppercase' }}>remaining</div>
        </div>
      </div>
    );
  })();

  const statusPillJsx = (() => {
    const x = STATUS_COLORS[paymentStatus] || STATUS_COLORS.waiting;
    return (
      <span className="ck-pill" style={{ background: x.bg, color: x.c, border: `1px solid ${x.b}` }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: x.c, animation: (paymentStatus === 'waiting' || paymentStatus === 'confirming') ? 'ck-dot 1.5s ease-in-out infinite' : 'none' }} />
        {STATUS_LABELS[paymentStatus] || 'Awaiting Payment'}
      </span>
    );
  })();

  const reviewBlockJsx = (() => {
    if (boostStatus !== 'completed' && boostStatus !== 'failed') return null;
    const ok = boostStatus !== 'failed';
    if (!ok) return null;

    if (reviewSubmitted) return (
      <div className="ck-glass" style={{ maxWidth: 380, width: '100%', textAlign: 'center', padding: '1.25rem', marginTop: '1.25rem' }}>
        <span style={{ fontSize: '1.1rem' }}>🎉</span>
        <div style={{ fontSize: '.8rem', color: 'var(--accent)', fontWeight: 600, marginTop: 6 }}>
          {reviewCoupon ? <>Your {reviewDiscount}% coupon: <code style={{ background: 'rgba(46,232,197,.12)', padding: '2px 8px', borderRadius: 6 }}>{reviewCoupon}</code></> : 'Thank you for your review!'}
        </div>
      </div>
    );

    return (
      <div className="ck-glass" style={{ maxWidth: 400, width: '100%', textAlign: 'center', padding: '1.5rem', marginTop: '1.25rem', background: 'linear-gradient(135deg,rgba(46,232,197,.04) 0%,rgba(139,92,246,.04) 100%)', border: '1px solid rgba(46,232,197,.1)' }}>
        <div style={{ fontSize: '.9rem', fontWeight: 700, marginBottom: 4 }}>⭐ Earn a <span style={{ color: 'var(--accent)' }}>{reviewDiscount}% discount</span></div>
        <p style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginBottom: '1rem', lineHeight: 1.45 }}>Leave a review and get a coupon code instantly</p>
        {isLoggedIn && canReview ? (<>
          <div style={{ display: 'flex', gap: 2, justifyContent: 'center', marginBottom: '.65rem' }}>
            {[1,2,3,4,5].map(s => (
              <button key={s} onClick={() => setReviewForm(f => ({ ...f, stars: s }))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.35rem', color: s <= reviewForm.stars ? '#f5a623' : 'rgba(255,255,255,.1)', padding: '0 2px', transition: 'transform .15s' }}
                onMouseEnter={e => e.target.style.transform = 'scale(1.2)'}
                onMouseLeave={e => e.target.style.transform = 'scale(1)'}>★</button>
            ))}
          </div>
          <textarea value={reviewForm.text} onChange={e => setReviewForm(f => ({ ...f, text: e.target.value }))} placeholder="Share your experience… (min 10 chars)" rows="2"
            style={{ width: '100%', padding: '.6rem .8rem', background: 'rgba(0,0,0,.25)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 10, color: 'var(--text-primary)', fontSize: '.75rem', resize: 'none', marginBottom: '.65rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color .2s' }}
            onFocus={e => e.target.style.borderColor = 'rgba(46,232,197,.2)'}
            onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,.07)'} />
          <button onClick={submitReview} disabled={reviewLoading || reviewForm.text.length < 10}
            style={{ width: '100%', padding: '.65rem', background: reviewForm.text.length >= 10 ? 'var(--accent)' : 'rgba(255,255,255,.05)', border: 'none', borderRadius: 10, color: reviewForm.text.length >= 10 ? '#0a0a0f' : 'var(--text-muted)', fontSize: '.78rem', fontWeight: 700, cursor: reviewForm.text.length >= 10 ? 'pointer' : 'not-allowed', transition: 'all .25s' }}>
            {reviewLoading ? 'Submitting…' : 'Submit Review'}
          </button>
          {reviewError && <p style={{ color: '#f0485f', fontSize: '.7rem', marginTop: 8 }}>{reviewError}</p>}
        </>) : !isLoggedIn ? (
          <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'center' }}>
            <Link to="/register" className="ck-btn ck-btn-primary" style={{ padding: '.55rem 1.25rem', fontSize: '.78rem' }}>Sign Up</Link>
            <Link to="/login" className="ck-btn ck-btn-ghost" style={{ padding: '.55rem 1.25rem', fontSize: '.78rem' }}>Log In</Link>
          </div>
        ) : <Link to="/dashboard" className="ck-btn ck-btn-primary" style={{ padding: '.55rem 1.25rem', fontSize: '.78rem' }}>Go to Dashboard</Link>}
      </div>
    );
  })();

  /* ═══════════════════════════════════════
     RENDER STATES
     ═══════════════════════════════════════ */

  /* LOADING */
  if (loading) return (
    <main className="main-content"><div className="ck-page" style={{ justifyContent: 'center' }}>
      <Brand />
      <div className="ck-enter" style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '3px solid rgba(255,255,255,.08)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'ck-ring .8s linear infinite', margin: '0 auto 1.25rem' }} />
        <p style={{ color: 'var(--text-secondary)', fontSize: '.88rem' }}>Loading your order…</p>
      </div>
    </div></main>
  );

  /* ERROR */
  if (error) return (
    <main className="main-content"><div className="ck-page" style={{ justifyContent: 'center' }}>
      <Brand />
      <div className="ck-enter ck-center-state">
        <div className="ck-icon-ring" style={{ background: 'rgba(240,72,95,.08)', border: '2px solid rgba(240,72,95,.15)' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f0485f" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
        </div>
        <h2 className="ck-title">Something went wrong</h2>
        <p className="ck-sub" style={{ marginBottom: '1.5rem' }}>{error}</p>
        <Link to="/" className="ck-btn ck-btn-primary">Return Home</Link>
      </div>
    </div></main>
  );

  /* STRIPE/PAYPAL CONFIRMING */
  if ((stripeConfirming || (paymentSuccess && checkout?.paymentMethod === 'stripe')) && !checkout?.completed && boostStatus !== 'completed' && boostStatus !== 'failed') return (
    <main className="main-content"><div className="ck-page" style={{ justifyContent: 'center' }}>
      <Brand />
      <div className="ck-enter ck-center-state">
        <div className="ck-icon-ring" style={{ background: 'rgba(46,232,197,.06)', border: '2px solid rgba(46,232,197,.15)', animation: 'ck-breathe 2.5s ease-in-out infinite' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
        </div>
        <h2 className="ck-title">{paymentStatus === 'confirmed' ? (isToken ? 'Delivering Tokens…' : 'Processing Boosts…') : 'Confirming Payment…'}</h2>
        <p className="ck-sub" style={{ marginBottom: '1.5rem' }}>Your {checkout?.paymentMethod === 'paypal' ? 'PayPal' : 'card'} payment is being processed. This usually takes a few seconds.</p>
        <div style={{ width: 36, height: 36, border: '3px solid rgba(255,255,255,.08)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'ck-ring .8s linear infinite', margin: '0 auto 1.5rem' }} />
        <OrderTag orderId={orderId} />
      </div>
    </div></main>
  );

  /* STRIPE WAITING (not paid yet) */
  if (checkout?.paymentMethod === 'stripe' && checkout?.status === 'waiting' && !paymentSuccess) return (
    <main className="main-content"><div className="ck-page" style={{ justifyContent: 'center' }}>
      <Brand />
      <div className="ck-enter ck-center-state">
        <div className="ck-icon-ring" style={{ background: 'rgba(245,158,11,.06)', border: '2px solid rgba(245,158,11,.15)' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
        </div>
        <h2 className="ck-title">Payment Pending</h2>
        <p className="ck-sub" style={{ marginBottom: '1.25rem' }}>Your card payment was not completed.</p>
        <div className="ck-glass" style={{ maxWidth: 360, width: '100%', marginBottom: '1.5rem' }}>
          <div className="ck-row"><span className="ck-row-label">Product</span><span className="ck-row-val">{checkout.quantity}× {checkout.productName}</span></div>
          <div className="ck-row"><span className="ck-row-label">Amount</span><span className="ck-row-val" style={{ color: 'var(--accent)', fontWeight: 700 }}>${checkout.total?.toFixed(2)}</span></div>
        </div>
        <Link to="/" className="ck-btn ck-btn-ghost">Start Over</Link>
        <div style={{ marginTop: '1.25rem' }}><OrderTag orderId={orderId} /></div>
      </div>
    </div></main>
  );

  /* COMPLETED / FAILED */
  if (checkout?.completed || boostStatus === 'completed' || boostStatus === 'failed') {
    const ok = boostStatus !== 'failed';
    return (
      <main className="main-content"><div className="ck-page" style={{ justifyContent: 'center' }}>
        <Brand />
        <div className="ck-enter ck-center-state">
          <div className="ck-icon-ring" style={{ background: ok ? 'rgba(46,232,197,.06)' : 'rgba(240,72,95,.06)', border: `2px solid ${ok ? 'rgba(46,232,197,.15)' : 'rgba(240,72,95,.15)'}`, animation: ok ? 'ck-pulse 2s ease-in-out infinite' : 'none' }}>
            {ok && <Confetti />}
            {ok
              ? <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
              : <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#f0485f" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>}
          </div>
          <h2 className="ck-title">{ok ? (isTool ? 'License Key Delivered! 🔧' : isToken ? 'Tokens Delivered! 🎟️' : 'Boosts Delivered! 🎉') : 'Delivery Failed'}</h2>
          <p className="ck-sub" style={{ marginBottom: '1.25rem' }}>{ok ? (isTool ? 'Your license key is ready below.' : isToken ? 'Your tokens are ready below.' : 'Your boosts have been applied to your server.') : 'There was an issue with your order. Please contact support.'}</p>
          
          {/* LICENSE KEY DISPLAY */}
          {ok && isTool && orderLicenseKeys.length > 0 && (
            <div style={{ width: '100%', maxWidth: 420, margin: '0 auto 1.5rem', textAlign: 'left' }}>
              <div style={{ background: 'rgba(0,0,0,.35)', border: '1px solid rgba(46,232,197,.15)', borderRadius: 12, padding: '16px 18px', backdropFilter: 'blur(8px)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--accent, #2ee8c5)' }}>🔧 Your License Key{orderLicenseKeys.length > 1 ? 's' : ''} ({orderLicenseKeys.length})</span>
                  <button onClick={() => copy(orderLicenseKeys.join('\n'), 'all-keys')} style={{ background: 'rgba(46,232,197,.1)', border: '1px solid rgba(46,232,197,.2)', borderRadius: 6, padding: '4px 10px', color: 'var(--accent, #2ee8c5)', fontSize: '.7rem', cursor: 'pointer', fontWeight: 600 }}>
                    {copied['all-keys'] ? '✓ Copied' : 'Copy All'}
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {orderLicenseKeys.map((k, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(46,232,197,.04)', border: '1px solid rgba(46,232,197,.1)', borderRadius: 8, padding: '10px 12px' }}>
                      <code style={{ fontSize: '.76rem', color: 'var(--accent, #2ee8c5)', wordBreak: 'break-all', flex: 1, fontFamily: "'SF Mono',Monaco,Consolas,monospace", letterSpacing: '.5px' }}>{k}</code>
                      <button onClick={() => copy(k, `lk-${i}`)} style={{ background: 'none', border: 'none', color: copied[`lk-${i}`] ? 'var(--accent, #2ee8c5)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '.7rem', marginLeft: 8, whiteSpace: 'nowrap' }}>
                        {copied[`lk-${i}`] ? '✓' : '📋'}
                      </button>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(46,232,197,.04)', border: '1px solid rgba(46,232,197,.08)', borderRadius: 8, fontSize: '.68rem', color: '#8a8a9a', lineHeight: 1.6 }}>
                  <strong style={{ color: '#c0c0cc' }}>Next steps:</strong> Go to your <a href="/dashboard?tab=tools" style={{ color: 'var(--accent, #2ee8c5)', textDecoration: 'none', fontWeight: 600 }}>Dashboard → My Tools</a> to manage your license, download the tool, and reset HWID if needed.
                </div>
              </div>
            </div>
          )}

          {/* TOKEN DISPLAY */}
          {ok && isToken && orderTokens.length > 0 && (
            <div style={{ width: '100%', maxWidth: 420, margin: '0 auto 1.5rem', textAlign: 'left' }}>
              <div style={{ background: 'rgba(0,0,0,.35)', border: '1px solid rgba(0,229,208,.15)', borderRadius: 12, padding: '16px 18px', backdropFilter: 'blur(8px)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--accent)' }}>🎟️ Your Tokens ({orderTokens.length})</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => copy(orderTokens.join('\n'), 'all-tokens')} style={{ background: 'rgba(0,229,208,.1)', border: '1px solid rgba(0,229,208,.2)', borderRadius: 6, padding: '4px 10px', color: 'var(--accent)', fontSize: '.7rem', cursor: 'pointer', fontWeight: 600 }}>
                      {copied['all-tokens'] ? '✓ Copied' : 'Copy All'}
                    </button>
                    <button onClick={() => { const b = new Blob([orderTokens.join('\n')], { type: 'text/plain' }); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = `tokens-${orderId.slice(0,8)}.txt`; a.click(); }} style={{ background: 'rgba(139,92,246,.1)', border: '1px solid rgba(139,92,246,.2)', borderRadius: 6, padding: '4px 10px', color: '#a78bfa', fontSize: '.7rem', cursor: 'pointer', fontWeight: 600 }}>
                      ↓ Download
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {orderTokens.map((t, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 8, padding: '8px 12px' }}>
                      <code style={{ fontSize: '.72rem', color: 'var(--text-primary)', wordBreak: 'break-all', flex: 1 }}>{t}</code>
                      <button onClick={() => copy(t, `tk-${i}`)} style={{ background: 'none', border: 'none', color: copied[`tk-${i}`] ? 'var(--accent)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '.7rem', marginLeft: 8, whiteSpace: 'nowrap' }}>
                        {copied[`tk-${i}`] ? '✓' : '📋'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <OrderTag orderId={orderId} />
          <p style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            For full details, <Link to={`/order/${orderId}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>check order status</Link>.
          </p>
          {reviewBlockJsx}
          <div style={{ display: 'flex', gap: '.65rem', justifyContent: 'center', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <Link to="/" className="ck-btn ck-btn-primary">Return Home</Link>
            <Link to={`/order/${orderId}`} className="ck-btn ck-btn-ghost">View Status</Link>
          </div>
        </div>
      </div></main>
    );
  }

  /* EXPIRED */
  if (checkout?.expired) return (
    <main className="main-content"><div className="ck-page" style={{ justifyContent: 'center' }}>
      <Brand />
      <div className="ck-enter ck-center-state">
        <div className="ck-icon-ring" style={{ background: 'rgba(245,158,11,.06)', border: '2px solid rgba(245,158,11,.15)' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
        </div>
        <h2 className="ck-title">Payment Expired</h2>
        <p className="ck-sub" style={{ marginBottom: '1.25rem' }}>The payment window for this order has closed.</p>
        <div className="ck-glass" style={{ maxWidth: 360, width: '100%', marginBottom: '1.5rem' }}>
          <div className="ck-row"><span className="ck-row-label">Product</span><span className="ck-row-val">{checkout.quantity}× {checkout.productName}</span></div>
          <div className="ck-row"><span className="ck-row-label">Amount</span><span className="ck-row-val" style={{ color: 'var(--accent)', fontWeight: 700 }}>${checkout.total?.toFixed(2)}</span></div>
        </div>
        <Link to="/" className="ck-btn ck-btn-primary">Create New Order</Link>
      </div>
    </div></main>
  );

  /* ═══════════════════════════════════════
     MAIN CRYPTO CHECKOUT
     ═══════════════════════════════════════ */
  const cryptoAmount = checkout?.cryptoAmount || '0';
  const cryptoType = checkout?.cryptoType || 'LTC';
  const payCurrency = checkout?.payCurrency || 'ltc';
  const cryptoName = CRYPTO_NAMES[cryptoType] || cryptoType;
  const cryptoSymbol = CRYPTO_SYMBOLS[payCurrency] || payCurrency?.toUpperCase() || 'CRYPTO';
  const paymentAddress = checkout?.paymentAddress || '';
  const cryptoColor = CRYPTO_COLORS[cryptoType] || 'var(--accent)';

  return (
    <main className="main-content"><div className="ck-page">
      <Brand />

      {/* Title */}
      <div className="ck-enter" style={{ textAlign: 'center', marginBottom: '1rem', zIndex: 1 }}>
        <h1 className="ck-title" style={{ background: 'linear-gradient(135deg,#fff 30%,rgba(46,232,197,.85))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Complete Your Payment
        </h1>
        <p className="ck-sub" style={{ marginBottom: '.5rem' }}>Send the exact amount to the address below</p>
      </div>

      <StepperView step={step} />

      <div className="ck-grid">
        {/* ── LEFT COLUMN: Payment ── */}
        <div className="ck-glass ck-enter ck-d2">
          <div className="ck-glass-glow" style={{ background: `linear-gradient(90deg,transparent,${cryptoColor},transparent)` }} />

          {/* Timer ring */}
          {timerRingJsx}

          {/* Amount display */}
          <div style={{ textAlign: 'center', padding: '1.25rem 1rem', borderRadius: 16, background: `linear-gradient(145deg,${cryptoColor}08,rgba(139,92,246,.03))`, border: `1px solid ${cryptoColor}18`, marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>Send Exactly</div>
            <div style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 'clamp(1.5rem,4vw,2.1rem)', fontWeight: 800, color: cryptoColor, letterSpacing: '-.02em' }}>{cryptoAmount}</span>
              <span style={{ fontSize: '.9rem', fontWeight: 500, color: 'var(--text-secondary)', marginLeft: 6 }}>{cryptoSymbol}</span>
            </div>
            <button onClick={() => copy(cryptoAmount, 'amount')} className={`ck-copy${copied.amount ? ' copied' : ''}`}>
              {copied.amount ? '✓ Copied!' : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg> Copy Amount</>}
            </button>
          </div>

          {/* QR Code */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
            {paymentAddress ? (
              <div style={{ padding: 10, borderRadius: 16, background: '#fff', boxShadow: `0 8px 32px rgba(0,0,0,.35), 0 0 0 1px ${cryptoColor}15` }}>
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(paymentAddress)}`} alt="QR" style={{ width: 150, height: 150, borderRadius: 8, display: 'block' }} />
              </div>
            ) : (
              <div style={{ width: 150, height: 150, borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(255,255,255,.03)', color: 'var(--text-muted)', fontSize: '.75rem' }}>
                <div style={{ width: 24, height: 24, border: '2px solid rgba(255,255,255,.1)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'ck-ring .8s linear infinite' }} />
                Generating…
              </div>
            )}
          </div>

          {/* Address */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>
              Payment Address ({cryptoSymbol})
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.6rem .8rem', background: 'rgba(0,0,0,.25)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 12 }}>
              <code style={{ flex: 1, fontSize: '.65rem', wordBreak: 'break-all', color: 'var(--text-primary)', fontFamily: 'monospace', lineHeight: 1.5 }}>{paymentAddress || 'Loading…'}</code>
              <button onClick={() => copy(paymentAddress, 'address')} className={`ck-copy${copied.address ? ' copied' : ''}`}>
                {copied.address ? '✓' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Important notice */}
          <div style={{ padding: '.7rem .9rem', borderRadius: 12, background: 'rgba(245,158,11,.04)', border: '1px solid rgba(245,158,11,.1)', fontSize: '.7rem', color: 'rgba(245,158,11,.75)', lineHeight: 1.55, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            <span><strong>Important:</strong> Send the exact amount. Network fees are paid separately.</span>
          </div>
        </div>

        {/* ── RIGHT COLUMN: Order Summary ── */}
        <div className="ck-glass ck-enter ck-d3">
          <div className="ck-glass-glow" style={{ background: 'linear-gradient(90deg,transparent,var(--accent),transparent)' }} />

          <div style={{ fontSize: '.9rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>
            Order Summary
          </div>

          <div className="ck-row">
            <span className="ck-row-label">Order ID</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <code style={{ fontSize: '.67rem', background: 'rgba(255,255,255,.04)', padding: '3px 7px', borderRadius: 5, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orderId}</code>
              <button onClick={() => copy(orderId, 'orderId')} className={`ck-copy${copied.orderId ? ' copied' : ''}`} style={{ padding: '3px 8px', fontSize: '.62rem' }}>
                {copied.orderId ? '✓' : 'Copy'}
              </button>
            </div>
          </div>

          <div className="ck-row">
            <span className="ck-row-label">Product</span>
            <span className="ck-row-val">{checkout?.quantity}× {checkout?.productName}</span>
          </div>

          <div className="ck-row">
            <span className="ck-row-label">Payment</span>
            <span className="ck-row-val" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: cryptoColor }} />
              {cryptoName}
            </span>
          </div>

          <div className="ck-row">
            <span className="ck-row-label">Amount (USD)</span>
            <span className="ck-row-val" style={{ color: 'var(--accent)', fontWeight: 800, fontSize: '.95rem' }}>${checkout?.total?.toFixed(2)}</span>
          </div>

          <div className="ck-row" style={{ borderBottom: 'none' }}>
            <span className="ck-row-label">Status</span>
            {statusPillJsx}
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: 'rgba(255,255,255,.04)', margin: '.5rem 0' }} />

          {/* History button */}
          <button onClick={() => setShowHistory(true)} style={{ width: '100%', padding: '.65rem', marginTop: '.5rem', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 12, color: 'var(--text-secondary)', fontSize: '.78rem', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer', transition: 'all .2s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,.12)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,.06)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Order Timeline
          </button>

          {/* Warning */}
          <div style={{ marginTop: '.75rem', padding: '.65rem .85rem', borderRadius: 12, background: 'rgba(245,158,11,.03)', border: '1px solid rgba(245,158,11,.08)', fontSize: '.7rem', color: 'rgba(245,158,11,.65)', lineHeight: 1.55, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            <span>Payment is detected automatically. Do not close this page.</span>
          </div>
        </div>
      </div>

      {/* ── HISTORY MODAL ── */}
      {showHistory && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: '1rem' }} onClick={() => setShowHistory(false)}>
          <div className="ck-glass ck-enter" style={{ maxWidth: 460, width: '100%', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Order Timeline
              </h3>
              <button onClick={() => setShowHistory(false)} style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', cursor: 'pointer', transition: 'all .2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.08)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,.05)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            {history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" style={{ marginBottom: 12, opacity: .5 }}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Waiting for payment…</p>
              </div>
            ) : (
              <div style={{ position: 'relative', paddingLeft: 20 }}>
                {/* Timeline line */}
                <div style={{ position: 'absolute', left: 4, top: 8, bottom: 8, width: 2, background: 'rgba(46,232,197,.15)', borderRadius: 1 }} />
                {history.map((item, idx) => (
                  <div key={idx} style={{ position: 'relative', padding: '.75rem 0 .75rem .75rem' }}>
                    <div style={{ position: 'absolute', left: -16, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', background: idx === history.length - 1 ? 'var(--accent)' : 'rgba(46,232,197,.3)', border: '2px solid rgba(10,10,15,.8)', boxShadow: idx === history.length - 1 ? '0 0 8px rgba(46,232,197,.4)' : 'none' }} />
                    <div style={{ fontWeight: 600, fontSize: '.82rem', marginBottom: 2 }}>{item.event}</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{item.details}</div>
                    <div style={{ fontSize: '.62rem', color: 'var(--text-muted)', marginTop: 3 }}>{item.time}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div></main>
  );
};

export default Checkout;
